/**
 * useContatori — i numeri accanto alle voci del menu (stile «Console»,
 * fase 1, punto 4; Alex 2/10/2026).
 *
 * Li chiede a UNA rotta, GET /api/contatori, che li calcola sul server,
 * ognuno da una definizione sola (backend/controllers/contatoriController.js):
 *   compiti   i miei compiti aperti (non quelli che ho scritto per altri)
 *   fatture   da correggere e scartate - solo a chi legge le fatture
 *   agenda    in sala d'attesa: arrivati oggi e il medico non ha ancora
 *             scritto nella visita - a un medico i suoi, ad
 *             amministrazione e segreteria tutti
 *   pazienti  anagrafiche da completare di chi ha un appuntamento da oggi
 *             a sette giorni - solo al front-desk
 * Qui non si conta niente: nessun contatore calcolato nel browser, e
 * nessun contatore senza un controllo dietro (domanda 3, Alex 2/10/2026).
 *
 * QUANDO SI CHIEDONO: all'apertura del guscio, a ogni cambio di schermata
 * (chi ha appena chiuso un compito e passa a un'altra schermata deve
 * vedere il numero nuovo, non quello di un minuto fa), e ogni minuto, per
 * quello che cambia senza di lui: un paziente arriva, una fattura viene
 * scartata.
 *
 * CHE COSA RESTITUISCE: { compiti, fatture, agenda, pazienti }, solo le
 * chiavi di CONTATORI (navigazione/schermate.js) e solo interi non
 * negativi. Una voce del menu mostra il numero della sua chiave
 * `contatore`. Una chiave che manca - il ruolo non la vede, la rotta non
 * ha risposto, il valore non e' un numero - vuol dire nessun numero, non
 * zero: il menu non scrive niente. Se la rotta non risponde, i numeri
 * spariscono invece di restare quelli di prima: un numero vecchio sembra
 * vero.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { periodoAvvisi } from '../utils/periodoAvvisi';
import { CONTATORI } from '../navigazione/schermate';

const OGNI = 60 * 1000;
const VUOTO = Object.freeze({});

/** Dalla risposta del server, solo le chiavi ammesse con un intero non negativo. */
export function soloNumeri(dati) {
    if (!dati || typeof dati !== 'object') return VUOTO;
    const fuori = {};
    for (const chiave of CONTATORI) {
        const v = dati[chiave];
        if (Number.isSafeInteger(v) && v >= 0) fuori[chiave] = v;
    }
    return Object.keys(fuori).length ? Object.freeze(fuori) : VUOTO;
}

/* Lo stesso contenuto non ridisegna il guscio: la richiesta di ogni minuto
   torna quasi sempre uguale. */
const uguali = (a, b) => {
    const ka = Object.keys(a);
    return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
};

export default function useContatori() {
    const { pathname } = useLocation();
    const [contatori, setContatori] = useState(VUOTO);
    /* La richiesta piu' recente: una risposta lenta non scrive sopra una
       arrivata dopo di lei. */
    const ultima = useRef(0);
    const vivo = useRef(true);

    const carica = useCallback(async () => {
        ultima.current += 1;
        const mia = ultima.current;
        let nuovi = VUOTO;
        try {
            const r = await api.get('/contatori');
            nuovi = soloNumeri(r?.data?.data?.contatori);
        } catch (e) {
            nuovi = VUOTO;
        }
        if (vivo.current && mia === ultima.current) {
            setContatori((prima) => (uguali(prima, nuovi) ? prima : nuovi));
        }
    }, []);

    useEffect(() => {
        vivo.current = true;
        /* Lo stesso orologio degli avvisi del guscio (utils/periodoAvvisi.js):
           sono le letture periodiche che si svegliano insieme al controllo
           della versione (utils/versioneInLinea.js), e le prove dei percorsi
           (scenario 15) le accorciano tutte insieme. Con un minuto suo questa
           restava fuori dalla prova delle letture in volo (unione console e
           ricarica, 3/10/2026). Senza l'oggetto delle prove: un minuto. */
        const t = setInterval(carica, periodoAvvisi(OGNI));
        return () => { vivo.current = false; clearInterval(t); };
    }, [carica]);

    useEffect(() => { carica(); }, [carica, pathname]);

    return contatori;
}
